import type { RiskAssessment, RiskAssessmentFilters } from '@nestjs-boilerplate/types';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { ArrowUpDown } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DataCardList } from '@/components/ui/data-card-list';
import { DataTable } from '@/components/ui/data-table';
import { MobileSortControl, type MobileSortOption } from '@/components/ui/mobile-sort-control';
import { useIsMobile } from '@/hooks/use-is-mobile';

import { useRestoreRiskAssessment, useRiskAssessments } from '../api/risk-assessments';
import { Pagination } from './pagination';
import { RiskLevelBadge } from './risk-level-badge';
import { SearchInput } from './search-input';

const DELETED_SORT_OPTIONS: MobileSortOption[] = [
  { id: 'clientRef', label: 'Client Ref', descLabel: 'Z → A', ascLabel: 'A → Z' },
  { id: 'riskLevel', label: 'Risk Level', descLabel: 'Z → A', ascLabel: 'A → Z' },
  {
    id: 'dateDeleted',
    label: 'Date Deleted',
    descLabel: 'Newest first',
    ascLabel: 'Oldest first',
  },
];

function compareSortable(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() - b.getTime();
  }
  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }
  return String(a).localeCompare(String(b));
}

function formatDate(date: Date | null): string {
  if (!date) return '-';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(date));
}

interface DeletedAssessmentsContentProps {
  onViewDetails?: (assessment: RiskAssessment) => void;
}

export function DeletedAssessmentsContent({ onViewDetails }: DeletedAssessmentsContentProps) {
  const isMobile = useIsMobile();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sorting, setSorting] = useState<SortingState>([]);

  // Build API filters for deleted assessments
  const apiFilters: Partial<RiskAssessmentFilters> = useMemo(
    () => ({
      status: 'DELETED',
      search: search || undefined,
      page,
      pageSize,
    }),
    [search, page, pageSize],
  );

  // API hooks
  const { data, isLoading, isError } = useRiskAssessments(apiFilters);
  const restoreMutation = useRestoreRiskAssessment();

  // Extract data from API response
  const assessments = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 0;

  // Client-side sort of the current page so both desktop table headers and the
  // mobile sort control share the same sorted view. Keeps layouts in sync across
  // resize. Sorting is only applied to the current page (server returns a page at a time).
  const sortedAssessments = useMemo(() => {
    if (sorting.length === 0) return assessments;
    const { id, desc } = sorting[0];
    const multiplier = desc ? -1 : 1;
    return [...assessments].sort((a, b) => {
      const av = (a as unknown as Record<string, unknown>)[id];
      const bv = (b as unknown as Record<string, unknown>)[id];
      return multiplier * compareSortable(av, bv);
    });
  }, [assessments, sorting]);

  // Handle restore
  const handleRestore = (assessment: RiskAssessment) => {
    restoreMutation.mutate(assessment.id);
  };

  // Reset page when search changes
  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const columns: ColumnDef<RiskAssessment>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'optimalKycRef',
      header: 'Optimal KYC Ref',
      cell: ({ row }) => (
        <span className="font-medium text-[#051923]">{row.getValue('optimalKycRef')}</span>
      ),
    },
    {
      accessorKey: 'clientRef',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-4 h-auto p-0 hover:bg-transparent"
        >
          Client Ref
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => <span className="text-[#051923]">{row.getValue('clientRef')}</span>,
    },
    {
      accessorKey: 'clientName',
      header: 'Client Name',
      cell: ({ row }) => <span className="text-[#051923]">{row.getValue('clientName')}</span>,
    },
    {
      accessorKey: 'riskLevel',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-4 h-auto p-0 hover:bg-transparent"
        >
          Client Risk Level
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => <RiskLevelBadge level={row.getValue('riskLevel')} />,
    },
    {
      id: 'documentRequest',
      header: 'Document Request Link Generated',
      cell: ({ row }) => {
        const assessment = row.original;
        const hasDocumentRequests =
          assessment.documentRequests && assessment.documentRequests.length > 0;

        if (!hasDocumentRequests) {
          return <span className="text-[#051923]">No</span>;
        }

        return (
          <div className="flex items-center gap-2">
            <span className="text-[#051923]">Yes</span>
            <button
              type="button"
              onClick={() => onViewDetails?.(assessment)}
              className="text-[#051923] underline hover:no-underline"
            >
              View Details
            </button>
          </div>
        );
      },
    },
    {
      accessorKey: 'dateDeleted',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-4 h-auto p-0 hover:bg-transparent"
        >
          Date Deleted
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <span className="text-[#051923]">{formatDate(row.getValue('dateDeleted'))}</span>
      ),
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const assessment = row.original;

        return (
          <button
            type="button"
            onClick={() => handleRestore(assessment)}
            className="font-medium text-[#051923] underline hover:no-underline"
          >
            Restore
          </button>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-[24px] font-semibold text-[#051923]">Deleted Risk Assessments</h1>
        <p className="mt-2 text-[16px] text-muted-foreground">
          Deleted Risk Assessments can be restored for up to 10 days. If not restored during this
          period, the assessments will be permanently deleted in line with our data retention
          policy.
        </p>
      </div>

      <div className="flex items-center justify-between">
        <SearchInput value={search} onChange={handleSearchChange} />
        <span className="text-[14px] text-muted-foreground">{total} Results</span>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">Loading deleted assessments...</div>
        </div>
      )}

      {/* Error state */}
      {isError && (
        <div className="flex items-center justify-center py-12">
          <div className="text-destructive">
            Failed to load deleted assessments. Please try again.
          </div>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !isError && assessments.length === 0 && (
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">No deleted assessments found.</div>
        </div>
      )}

      {/* Table / Card list */}
      {!isLoading && !isError && assessments.length > 0 && (
        <>
          {isMobile ? (
            <>
              <MobileSortControl
                options={DELETED_SORT_OPTIONS}
                value={sorting[0] ? { id: sorting[0].id, desc: sorting[0].desc } : undefined}
                onChange={(next) => setSorting([{ id: next.id, desc: next.desc }])}
              />
              <DataCardList<RiskAssessment>
                data={sortedAssessments}
                getRowId={(row) => row.id}
                cardConfig={{
                  header: (row) => (
                    <div className="flex flex-col gap-0.5">
                      <span className="font-semibold text-[#051923]">{row.optimalKycRef}</span>
                      <span className="text-sm text-muted-foreground">{row.clientName}</span>
                    </div>
                  ),
                  fields: [
                    { label: 'Client Ref', render: (row) => row.clientRef },
                    {
                      label: 'Risk Level',
                      render: (row) => <RiskLevelBadge level={row.riskLevel} />,
                    },
                    {
                      label: 'Document Request',
                      render: (row) => {
                        const hasUploads = row.documentRequests && row.documentRequests.length > 0;
                        if (!hasUploads) {
                          return <span className="text-[#051923]">No</span>;
                        }
                        return (
                          <span data-no-row-click className="inline-flex items-center gap-2">
                            <span className="text-[#051923]">Yes</span>
                            <button
                              type="button"
                              onClick={() => onViewDetails?.(row)}
                              className="text-[#051923] underline hover:no-underline"
                            >
                              View Details
                            </button>
                          </span>
                        );
                      },
                    },
                    {
                      label: 'Date Deleted',
                      render: (row) => formatDate(row.dateDeleted),
                    },
                  ],
                  footer: (row) => (
                    <div className="flex justify-end" data-no-row-click>
                      <button
                        type="button"
                        onClick={() => handleRestore(row)}
                        className="font-medium text-[#051923] underline hover:no-underline"
                      >
                        Restore
                      </button>
                    </div>
                  ),
                }}
              />
            </>
          ) : (
            <DataTable
              columns={columns}
              data={sortedAssessments}
              enableRowSelection
              getRowId={(row) => row.id}
              sorting={sorting}
              onSortingChange={setSorting}
            />
          )}

          {/* Pagination */}
          {total > 0 && (
            <Pagination
              page={page}
              pageSize={pageSize}
              total={total}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          )}
        </>
      )}
    </div>
  );
}
